import { Theme } from "@mui/material";
import { format } from "date-fns";
import { HomepageSparklineDay } from "../../../graphql/types";
import { EnabledStatus, InfoStatus } from "./types";

const TODAY_STRING = `${format(new Date(), "yyyy-MM-dd")}T00:00`;

enum SparklineStatus {
  // these should match HomepageCache.scala
  Retrying = "retrying",
  Clean = "clean",
  Warning = "warning",
  Issue = "issue",
  Failed = "failed",
  Missing = "missing",
  Expected = "expected",
}
const convertSparklineStatusToFilterStatus = (
  status: SparklineStatus,
  running: boolean,
  dependencyStatus: string
): number => {
  if (status == SparklineStatus.Failed || dependencyStatus === "suspended") {
    return 3;
  }
  if (status == SparklineStatus.Issue) {
    return 2;
  }
  if (status == SparklineStatus.Warning || status == SparklineStatus.Missing || dependencyStatus === "blocked") {
    return 1;
  }
  if (status == SparklineStatus.Clean || status == SparklineStatus.Expected || running) {
    return 0;
  }
  return -1;
};

const convertStatusNumberToFilterStatus = (status: number): keyof InfoStatus => {
  switch (status) {
    case 0:
      return "Working";
    case 1:
      return "Notice";
    case 2:
      return "Issue";
    case 3:
      return "Error";
    default:
      throw new Error(`Unknown status number: ${status}`);
  }
};
const enabledStatusOrdinal = (status: EnabledStatus): number => {
  if (status.working) {
    return 0;
  }
  if (status.warning) {
    return 1;
  }
  if (status.issue) {
    return 2;
  }
  if (status.error) {
    return 3;
  }
  return -1;
};

const shouldDownloadOrUploadBeShown = (
  enabledStatus: EnabledStatus,
  sparkline: Array<{ status: string; running: boolean }>,
  dependencyStatus: string
): {
  show: boolean;
  statusName: keyof InfoStatus;
} => {
  let sparklineStatus = -1;
  sparkline.forEach((day) => {
    sparklineStatus = Math.max(
      sparklineStatus,
      convertSparklineStatusToFilterStatus(day.status as SparklineStatus, day.running, dependencyStatus)
    );
  });

  const enabledStatusValue = enabledStatusOrdinal(enabledStatus);

  return {
    show: sparklineStatus >= enabledStatusValue,
    statusName: convertStatusNumberToFilterStatus(sparklineStatus),
  };
};

const getSparklineColor = (status: string, theme: Theme): string => {
  switch (status) {
    case SparklineStatus.Clean:
      return theme.palette.accentSuccess20.main;
    case SparklineStatus.Warning:
      return theme.palette.accentInfo20.main;
    case SparklineStatus.Issue:
      return theme.palette.accentWarning20.main;
    case SparklineStatus.Failed:
      return theme.palette.accentDanger20.main;
    case SparklineStatus.Missing:
      return theme.palette.surfaceLight50.main;
    default:
      return theme.palette.surfaceLight10.main;
  }
};

const hasAllDaysMissing = (sparkline: HomepageSparklineDay[]): boolean => {
  return (
    sparkline.slice(0, 6).every((day) => day.status === SparklineStatus.Missing) &&
    (sparkline[6].status === SparklineStatus.Expected || sparkline[6].status === SparklineStatus.Missing)
  );
};

export {
  enabledStatusOrdinal,
  getSparklineColor,
  hasAllDaysMissing,
  shouldDownloadOrUploadBeShown,
  SparklineStatus,
  TODAY_STRING,
};
